import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { ArrowDown, ArrowUpRight, User } from "lucide-react";

// Portrait Karim Kerkoub (depuis la capture envoyée, cadré sur le visage en N&B éditorial)
const PORTRAIT_URL =
  "https://customer-assets.emergentagent.com/job_laka-design-refresh/artifacts/xvbjwo1r_image.png";

const ease = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { t } = useLang();

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden"
    >
      {/* subtle grid bg */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #0A0A0A 1px, transparent 1px), linear-gradient(to bottom, #0A0A0A 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 relative">
        {/* SVG filter — true selective color: grayscale base + isolated blue overlay */}
        <svg width="0" height="0" className="absolute" aria-hidden>
          <defs>
            <filter id="laka-sea-isolation" colorInterpolationFilters="sRGB">
              {/* Base layer — grayscale of the whole image */}
              <feColorMatrix in="SourceGraphic" type="saturate" values="0" result="grayBase" />

              {/* Alpha mask — high alpha where blue dominates (B > R and B > G) */}
              <feColorMatrix
                in="SourceGraphic"
                type="matrix"
                values="0 0 0 0 0
                        0 0 0 0 0
                        0 0 0 0 0
                        -1.2 -1.2 2.5 0 -0.15"
                result="blueMask"
              />

              {/* Keep original colors only where mask alpha is non-zero */}
              <feComposite in="SourceGraphic" in2="blueMask" operator="in" result="blueOnly" />

              {/* Slightly boost the blue saturation of extracted area */}
              <feColorMatrix in="blueOnly" type="saturate" values="1.6" result="blueBoost" />

              {/* Merge grayscale base + isolated blue */}
              <feMerge>
                <feMergeNode in="grayBase" />
                <feMergeNode in="blueBoost" />
              </feMerge>
            </filter>
          </defs>
        </svg>

        {/* overline row */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="flex items-center justify-between mb-10 md:mb-16"
        >
          <div className="flex items-center gap-3">
            <span className="w-8 h-px bg-[#0A0A0A]" />
            <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#0A0A0A]">
              {t.hero.overline}
            </span>
          </div>
          <span className="hidden md:block font-mono text-[10px] tracking-[0.25em] uppercase text-[#5C5C5C]">
            48°34'N · 7°45'E
          </span>
        </motion.div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          {/* Main title block */}
          <div className="col-span-12 lg:col-span-8">
            <h1 className="font-display font-light tracking-[-0.02em] leading-[0.95] text-[clamp(2.5rem,6.8vw,7rem)] text-[#0A0A0A] uppercase">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, ease, delay: 0.15 }}
                className="block overflow-hidden"
              >
                <span className="block">
                  <span className="text-[#DC2626]">L</span>oge
                </span>
              </motion.span>
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, ease, delay: 0.3 }}
                className="block overflow-hidden text-[#5C5C5C] font-extralight"
              >
                <span className="block">d'Architecture</span>
              </motion.span>
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, ease, delay: 0.45 }}
                className="block overflow-hidden font-normal"
              >
                <span className="block">
                  Kerkoub Abdelkrim
                  <span className="text-[#D35400]">_</span>
                </span>
              </motion.span>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.7 }}
              className="mt-8 md:mt-10 flex items-center gap-4"
            >
              <span className="w-12 h-px bg-[#0A0A0A]" />
              <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#0A0A0A]">
                Architecture & BIM — Strasbourg · Luxembourg
              </span>
            </motion.div>
          </div>

          {/* Right column: portrait + meta */}
          <div className="col-span-12 lg:col-span-4 space-y-6">
            <motion.div
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease, delay: 0.6 }}
              className="relative aspect-[4/5] overflow-hidden bg-[#0A0A0A]"
            >
              {PORTRAIT_URL ? (
                <div className="absolute inset-0 group/portrait">
                  {/* Portrait — B&W par défaut, couleur au hover */}
                  <img
                    src={PORTRAIT_URL}
                    alt="Karim Kerkoub — architecte"
                    className="laka-photo absolute inset-0 w-full h-full object-cover"
                    style={{
                      objectPosition: "78% 38%",
                      transform: "scale(2.4)",
                      transformOrigin: "78% 38%",
                    }}
                  />
                  {/* Léger gradient bas pour profondeur */}
                  <div
                    aria-hidden
                    className="absolute pointer-events-none inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, transparent 0%, transparent 65%, rgba(10,10,10,0.22) 100%)",
                    }}
                  />
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#0A0A0A] via-[#1A1A1A] to-[#0A0A0A] text-[#F7F7F9]/40">
                  <User size={56} strokeWidth={1} />
                  <p className="mt-4 font-mono text-[10px] tracking-[0.3em] uppercase">
                    Portrait à venir
                  </p>
                </div>
              )}

              {/* Technical overlays */}
              <div className="absolute top-4 left-4 right-4 flex justify-between font-mono text-[10px] tracking-[0.2em] uppercase text-white/80 z-10">
                <span>N° 001</span>
                <span>STR / LUX</span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 font-mono text-[10px] tracking-[0.2em] uppercase text-white/80 border-t border-white/30 pt-2 flex justify-between z-10">
                <span>ARCHITECTE</span>
                <span>EST. 2010</span>
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.8 }}
              className="text-base md:text-lg font-light leading-relaxed text-[#0A0A0A] max-w-md"
            >
              {t.hero.subtitle}
            </motion.p>
          </div>
        </div>

        {/* CTAs row */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 1 }}
          className="mt-14 md:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        >
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="hero-cta-projects"
              onClick={() => go("projects")}
              className="group inline-flex items-center gap-3 bg-[#0A0A0A] text-[#F7F7F9] px-6 py-4 text-sm tracking-wide hover:bg-[#D35400] transition-colors duration-500"
            >
              {t.hero.cta_primary}
              <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-500" />
            </button>
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="group inline-flex items-center gap-3 border border-[#0A0A0A] text-[#0A0A0A] px-6 py-4 text-sm tracking-wide hover:bg-[#0A0A0A] hover:text-[#F7F7F9] transition-colors duration-500"
            >
              {t.hero.cta_secondary}
              <ArrowUpRight size={16} />
            </Link>
          </div>

          <button
            data-testid="scroll-hint"
            onClick={() => go("about")}
            className="flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] hover:text-[#0A0A0A] transition-colors"
          >
            <ArrowDown size={14} className="animate-bounce" />
            {t.hero.scroll}
          </button>
        </motion.div>
      </div>
    </section>
  );
}
